import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CButton, CButtonGroup } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCart } from "@coreui/icons";
import type { MembershipTierDto } from "../../api/hooks/useMembershipTiers";
import { annualSavingsPct, type BillingCycle } from "../../api/hooks/useTradingStrategies";
import { useToast } from "../../context/useToast";
import { formatPriceUsd } from "../../lib/money";
import { useCartStore } from "../../state/cartStore";

/**
 * How to buy one of our tiers, laid out as a strategy's: the billing cycle, the price for it, then Add to cart.
 * Once it's in the cart the cycle still changes it there.
 */
export function MembershipBuyPanel({ tier }: { tier: MembershipTierDto }) {
  const { t } = useTranslation("memberships");
  const { showSuccess } = useToast();
  const cartLine = useCartStore((s) => s.membershipItems.find((item) => item.tierId === tier.id));
  const addMembership = useCartStore((s) => s.addMembership);
  const removeMembership = useCartStore((s) => s.removeMembership);
  const updateBillingCycle = useCartStore((s) => s.updateMembershipBillingCycle);
  // An upgrade or an extension checks out on its own, so nothing else can join the cart beside one.
  const cartHeldBySomethingElse = useCartStore((s) => s.upgrade !== null || s.extension !== null);
  const [pendingCycle, setPendingCycle] = useState<BillingCycle>("Monthly");

  const prices = { monthlyPriceUsd: tier.monthlyPriceUsd ?? 0, yearlyPriceUsd: tier.yearlyPriceUsd ?? 0 };
  const cycle = cartLine?.billingCycle ?? pendingCycle;
  const price = cycle === "Yearly" ? prices.yearlyPriceUsd : prices.monthlyPriceUsd;
  const savings = annualSavingsPct(prices);

  const chooseCycle = (chosen: BillingCycle) => {
    if (cartLine) {
      updateBillingCycle(
        tier.id,
        chosen,
        chosen === "Yearly" ? prices.yearlyPriceUsd : prices.monthlyPriceUsd,
      );
    } else {
      setPendingCycle(chosen);
    }
  };

  const addToCart = () => {
    addMembership({ tierId: tier.id, name: tier.name, billingCycle: cycle, priceUsd: price });
    showSuccess(t("buy.added", { tier: tier.name }));
  };

  return (
    <div className="d-flex flex-column align-items-stretch gap-3" style={{ minWidth: 260 }}>
      <div className="text-body-secondary text-uppercase small text-sm-end" style={{ letterSpacing: "0.06em" }}>
        {t("buy.cycle")}
      </div>
      <CButtonGroup className="btn-group-pill">
        <CButton
          color="primary"
          variant={cycle === "Monthly" ? undefined : "outline"}
          onClick={() => chooseCycle("Monthly")}
        >
          {t("buy.monthly")}
        </CButton>
        <CButton
          color="primary"
          variant={cycle === "Yearly" ? undefined : "outline"}
          onClick={() => chooseCycle("Yearly")}
        >
          {t("buy.yearly")}
          {savings > 0 && <span className="ms-1 small">{t("buy.save", { percent: savings })}</span>}
        </CButton>
      </CButtonGroup>
      <div className="text-sm-end" style={{ fontSize: "2.25rem", fontWeight: 700, lineHeight: 1.1 }}>
        {formatPriceUsd(price)}
        <span className="text-body-secondary" style={{ fontSize: "0.95rem", fontWeight: 400 }}>
          {" "}
          {cycle === "Yearly" ? t("buy.perYear") : t("buy.perMonth")}
        </span>
      </div>
      {cartLine ? (
        <CButton color="danger" variant="outline" onClick={() => removeMembership(tier.id)}>
          {t("buy.remove")}
        </CButton>
      ) : (
        <CButton color="primary" disabled={cartHeldBySomethingElse} onClick={addToCart}>
          <CIcon icon={cilCart} className="me-2" />
          {t("buy.add")}
        </CButton>
      )}
      <div className="small text-body-secondary text-sm-end">
        {cartHeldBySomethingElse && !cartLine
          ? t("buy.cartHeld")
          : cycle === "Yearly"
            ? t("buy.yearlyNote")
            : t("buy.monthlyNote")}
      </div>
    </div>
  );
}
